import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CompanyAdminLayout } from "../../../components/CompanyAdmin/CompanyAdminLayout";
import { ErrorMessageModal } from "../../../components/UI/ErrorMessageModal";
import { CompanyOrderRepositoryImpl } from "../../../../infrastructure/repositories/company/company-order-repository-impl";
import { CompanyOrderUsecase } from "../../../../domain/usecases/company/company-order-usecase";
import {
  companyUseCase,
  shopProductUseCase,
  companyOrderUseCase,
  companyShopUseCase,
} from "../../../../di/container";
import type {
  CreateOrderRequest,
  OrderOption,
  Courier,
} from "../../../../domain/types/orders";
import type {
  ShopProduct,
  ProductOptionResponse,
  SampleValue,
  CombinationRequest,
  ProductOptionRequest,
  CalculatePriceResponse,
} from "../../../../domain/types/shop-product";
import { OrderCreateForm } from "../../../components/CompanyAdmin/OrderCreateForm";
import { CustomOrderCreateForm } from "../../../components/CompanyAdmin/CustomOrderCreateForm";

import type { Order, OrderStatus, CompanyMember } from "./types";
import { mapApiOrderToView, uiStatusToApiStatus } from "./utils";
import { ORDER_STATUS_LABELS } from "./constants";
import { OrderDetail } from "./components/OrderDetail";
import { OrderList } from "./components/OrderList";
import { ResubmissionDialog } from "./components/ResubmissionDialog";
import { ShippingDialog } from "./components/ShippingDialog";

import { useOrderList } from "./useOrderList";
import { orderDateBounds } from "./exportOrders";
import { OrderAmountSummaryDialog } from "./components/OrderAmountSummaryDialog";

const OrderManagement: React.FC = () => {
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const repo = useMemo(() => new CompanyOrderRepositoryImpl(), []);
  const usecase = useMemo(() => new CompanyOrderUsecase(repo), [repo]);

  const [viewMode, setViewMode] = useState<
    "list" | "detail" | "create" | "createCustom"
  >("list");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [errorMessageTitle, setErrorMessageTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [isResetButtonEnabled, setIsResetButtonEnabled] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<
    OrderStatus | "all"
  >("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isStatisticsOpen, setIsStatisticsOpen] = useState(false);
  const closeStatistics = useCallback(() => setIsStatisticsOpen(false), []);
  const { dateFrom, dateTo } = orderDateBounds(startDate, endDate);
  const fetchSearchSuggestions = useCallback(
    (keyword: string) => usecase.getOrderSearchSuggestions(Number(companyId), keyword),
    [usecase, companyId],
  );
  const statisticsConditionLabels = [
    searchKeyword.trim() && `검색어 '${searchKeyword.trim()}'`,
    (startDate || endDate) && `${(startDate || endDate).split("-").join(".")} ~ ${(endDate || startDate).split("-").join(".")}`,
    selectedOrderStatus !== "all" && `상태 ${ORDER_STATUS_LABELS[selectedOrderStatus]}`,
  ].filter((label): label is string => !!label);
  const dateRangeInvalid = !!(startDate && endDate && startDate > endDate);

  const {
    orders, hasMore, isLoadingMore, refreshError, refreshOrders, loadMoreOrders,
  } = useOrderList(usecase, Number(companyId), uiStatusToApiStatus(selectedOrderStatus),
    searchKeyword, dateFrom, dateTo, viewMode === "list");

  // 주문 생성 관련 상태
  const [shopId, setShopId] = useState<number | null>(null);
  const [shopProducts, setShopProducts] = useState<ShopProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ShopProduct | null>(
    null
  );
  const [productOptions, setProductOptions] =
    useState<ProductOptionResponse | null>(null);
  const [selectedCombinations, setSelectedCombinations] = useState<
    CombinationRequest[]
  >([]);
  const [calculatedPrice, setCalculatedPrice] =
    useState<CalculatePriceResponse | null>(null);

  // 회원 선택 관련 상태
  const [companyMembers, setCompanyMembers] = useState<CompanyMember[]>([]);

  // 리셋 버튼 비활성화/활성화 여부 관리
  useEffect(() => {
    if (searchKeyword !== "" || selectedOrderStatus !== "all" || startDate !== "" || endDate !== "") {
      setIsResetButtonEnabled(true);
    } else {
      setIsResetButtonEnabled(false);
    }
  }, [searchKeyword, selectedOrderStatus, startDate, endDate]);

  // 다이얼로그 상태
  const [isResubmissionOpen, setIsResubmissionOpen] = useState(false);
  const [resubmissionMessage, setResubmissionMessage] = useState("");

  const [isShippingDialogOpen, setIsShippingDialogOpen] = useState(false);
  const [shippingCourier, setShippingCourier] = useState("");
  const [shippingTrackingNumber, setShippingTrackingNumber] = useState("");
  const [courierList, setCourierList] = useState<Courier[]>([]);
  const [isCourierLoading, setIsCourierLoading] = useState(false);

  // 접수 처리
  const handleConfirmAcceptOrder = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("접수 요청 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.acceptOrder(cid, oid);
      await refreshOrders();

      alert("접수 되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("접수 요청 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 제작 시작
  const handleStartProduction = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("제작 시작 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.startProduction(cid, oid);
      await refreshOrders();

      alert("제작이 시작되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("제작 시작 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 제작 완료
  const handleCompleteProduction = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("제작 완료 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.completeProduction(cid, oid);
      await refreshOrders();

      alert("제작이 완료되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("제작 완료 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 배송 완료
  const handleCompleteDelivery = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("배송 완료 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.completeDelivery(cid, oid);
      await refreshOrders();

      alert("배송이 완료되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("배송 완료 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 출고/배송 다이얼로그 열기
  const handleOpenShippingDialog = async () => {
    setShippingCourier("");
    setShippingTrackingNumber("");
    setIsShippingDialogOpen(true);

    if (courierList.length === 0) {
      setIsCourierLoading(true);
      try {
        const couriers = await usecase.getCourierList();
        setCourierList(couriers);
      } catch (e: any) {
        console.error(e);
        setErrorMessageTitle("택배사 목록 로드 실패");
        setErrorMessage(e?.message ?? "택배사 목록을 불러오지 못했습니다.");
      } finally {
        setIsCourierLoading(false);
      }
    }
  };

  // 출고/배송 처리
  const handleConfirmShipping = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("출고/배송 처리 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.shipOrder(
        cid,
        oid,
        shippingCourier || undefined,
        shippingTrackingNumber || undefined
      );
      await refreshOrders();

      alert("출고/배송 처리가 완료되었습니다.");
      setIsShippingDialogOpen(false);
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("출고/배송 처리 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 주문 취소
  const handleCancelOrder = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("주문 취소 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      if (!window.confirm("주문을 취소하시겠습니까?")) {
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.cancelOrder(cid, oid);
      await refreshOrders();

      alert("주문이 취소되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("주문 취소 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 환불 처리
  const handleRefundOrder = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("환불 처리 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      if (!window.confirm("주문을 환불 처리하시겠습니까?")) {
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.refundOrder(cid, oid);
      await refreshOrders();

      alert("환불 처리가 완료되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("환불 처리 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 결제전 상태로 변경
  const handleResetToBeforePayment = async () => {
    try {
      if (!selectedOrder) {
        setErrorMessageTitle("상태 변경 실패");
        setErrorMessage("선택된 주문이 없습니다.");
        return;
      }

      if (!window.confirm("주문을 결제전 상태로 변경하시겠습니까?")) {
        return;
      }

      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.resetToBeforePayment(cid, oid);
      await refreshOrders();

      alert("결제전 상태로 변경되었습니다.");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("상태 변경 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 재접수 요청 다이얼로그 열기
  const handleRequestResubmission = (order: Order) => {
    setSelectedOrder(order);
    setResubmissionMessage("");
    setIsResubmissionOpen(true);
  };

  // 재접수 요청 확인
  const handleConfirmResubmission = async () => {
    if (!resubmissionMessage.trim()) {
      alert("수정 요청 사항을 입력해주세요");
      return;
    }

    if (resubmissionMessage.length > 200) {
      alert("메시지는 200자 이내로 입력해주세요");
      return;
    }

    if (!selectedOrder) {
      setErrorMessageTitle("재접수 요청 실패");
      setErrorMessage("선택된 주문이 없습니다.");
      return;
    }

    try {
      const cid = Number(companyId);
      const oid = Number(selectedOrder.id);
      if (!Number.isFinite(cid) || !Number.isFinite(oid)) {
        throw new Error("companyId 또는 orderId가 올바르지 않습니다.");
      }

      await usecase.resubmission(cid, oid, resubmissionMessage);
      await refreshOrders();

      alert("재접수 요청이 전송되었습니다");
      setIsResubmissionOpen(false);
      setResubmissionMessage("");
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("재접수 요청 실패");
      setErrorMessage(e?.message ?? "알 수 없는 오류가 발생했습니다.");
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = (action: string) => {
    if (!selectedOrder) return;

    switch (action) {
      case "pending_payment":
        handleResetToBeforePayment();
        break;
      case "accepted":
        handleConfirmAcceptOrder();
        break;
      case "resubmission":
        handleRequestResubmission(selectedOrder);
        break;
      case "production":
        handleStartProduction();
        break;
      case "production_complete":
        handleCompleteProduction();
        break;
      case "shipping":
        handleOpenShippingDialog();
        break;
      case "delivered":
        handleCompleteDelivery();
        break;
      case "cancelled":
        handleCancelOrder();
        break;
      case "refunded":
        handleRefundOrder();
        break;
    }
  };

  // URL 쿼리 파라미터를 읽어서 viewMode와 selectedOrder 설정
  useEffect(() => {
    const mode = searchParams.get("mode");
    const orderId = searchParams.get("orderId");
    let cancelled = false;

    if (mode === "create") {
      setViewMode("create");
    } else if (mode === "createCustom") {
      setViewMode("createCustom");
    } else if (mode === "detail" && orderId) {
      setViewMode("detail");
      const order = orders.find((o) => o.id === orderId);
      if (order) {
        setSelectedOrder(order);
      } else {
        // 알림함에서 연 지난 주문은 지금 불러온 목록에 없을 수 있다. 주문번호로 한 건 찾아 연다.
        const orderNumber = searchParams.get("orderNumber");
        if (orderNumber && companyId) {
          usecase.getOrders(Number(companyId), undefined, orderNumber, 0, 5)
            .then((result) => {
              const found = result.items.map(mapApiOrderToView).find((o) => o.id === orderId);
              if (!cancelled && found) setSelectedOrder(found);
            })
            .catch(() => {});
        }
      }
    } else {
      setViewMode("list");
      setSelectedOrder(null);
    }
    return () => { cancelled = true; };
  }, [searchParams, orders, companyId, usecase]);

  // 주문 생성 모드 진입 시 데이터 로드
  useEffect(() => {
    if ((viewMode === "create" || viewMode === "createCustom") && companyId) {
      (async () => {
        try {
          const cid = Number(companyId);

          if (viewMode === "create") {
            const shopInfo = await companyShopUseCase.getShopHome(cid);
            setShopId(shopInfo.shop.id);

            const products = await shopProductUseCase.getProducts(
              shopInfo.shop.id
            );
            setShopProducts(products);
          }

          let allMembers: CompanyMember[] = [];
          let pageNumber = 0;
          let hasMore = true;

          while (hasMore) {
            const response = await companyUseCase.getUserByCompanyId({
              companyId: cid,
              pageable: { page: pageNumber },
            });

            if (response.items.length < 1) {
              hasMore = false;
            } else {
              const members = response.items.map((item: any) => ({
                id: item.id,
                name: item.name,
                email: item.email,
                mobile: item.mobile || "",
              }));
              allMembers = [...allMembers, ...members];
              pageNumber++;
            }
          }

          setCompanyMembers(allMembers);
        } catch (e: any) {
          console.error(e);
          setErrorMessageTitle("데이터 로드 실패");
          setErrorMessage(e?.message ?? "데이터를 불러오지 못했습니다.");
        }
      })();
    }
  }, [viewMode, companyId]);

  // 상품 선택 시 옵션 정보 로드
  const handleProductSelect = async (productId: number) => {
    if (!shopId || productId === 0) {
      setSelectedProduct(null);
      setProductOptions(null);
      setSelectedCombinations([]);
      setCalculatedPrice(null);
      return;
    }

    try {
      const product = shopProducts.find((p) => p.id === productId);
      setSelectedProduct(product || null);

      const options = await shopProductUseCase.getProductOptions(
        shopId,
        productId
      );
      setProductOptions(options);
      setSelectedCombinations([]);
      setCalculatedPrice(null);

      if (product) {
        // Product 선택됨
      }
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("옵션 로드 실패");
      setErrorMessage(e?.message ?? "옵션 정보를 불러오지 못했습니다.");
    }
  };

  // 가격 재계산
  const handleCalculatePrice = async (
    combinations: CombinationRequest[],
    productCount: number
  ) => {
    if (!shopId || !selectedProduct) return;

    try {
      const request: ProductOptionRequest = {
        productId: selectedProduct.id,
        productCount,
        combinations,
      };

      const result = await shopProductUseCase.calculatePrice(
        shopId,
        selectedProduct.id,
        request
      );
      setCalculatedPrice(result);
      setSelectedCombinations(combinations);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("가격 계산 실패");
      setErrorMessage(e?.message ?? "가격 계산에 실패했습니다.");
    }
  };

  // 주문 생성 제출
  const handleCreateOrder = async (
    orderData: CreateOrderRequest,
    file: File | null,
    _selectedMemberId: number | null
  ) => {
    if (!companyId) return;

    try {
      const cid = Number(companyId);

      let finalOrderData: CreateOrderRequest;

      if (orderData.customOrderInfo) {
        finalOrderData = orderData;
      } else {
        const options: OrderOption[] = selectedCombinations.map((comb) => {
          const combination = productOptions?.combinations.find(
            (c) => c.id === comb.combinationId
          );
          const combinationKeyId =
            combination?.combinationKeys.join("_") || "";

          const snapshot = comb.attributeIds
            .map((attr) => {
              if (attr.value) return attr.value;
              if (attr.sampleValueId) {
                for (const opt of productOptions?.options || []) {
                  for (const attribute of opt.attributes) {
                    const sample = attribute.sampleValues?.find(
                      (s: SampleValue) => s.id === attr.sampleValueId
                    );
                    if (sample) return sample.value;
                  }
                }
              }
              return "";
            })
            .filter((v) => v)
            .join(", ");

          let quantityApplyType:
            | "PER_ITEM"
            | "PER_ORDER"
            | "SAME_AS_PRODUCT_QUANTITY"
            | "SELECTABLE" = "PER_ITEM";
          for (const opt of productOptions?.options || []) {
            if (
              opt.attributes.some((a) =>
                comb.attributeIds.some((aid) => aid.attributeId === a.id)
              )
            ) {
              quantityApplyType = opt.quantityApplyType;
              break;
            }
          }

          const optionPrice =
            calculatedPrice?.optionPrices.find((op) => op.optionName)
              ?.optionPrice || 0;

          return {
            combinationId: comb.combinationId,
            combinationKeyId,
            optionCount: comb.selectableCount || 1,
            quantityApplyType,
            snapshot,
            optionPrice,
          };
        });

        finalOrderData = {
          ...orderData,
          options: options.length > 0 ? options : undefined,
        };
      }

      const result = await companyOrderUseCase.createOrder(
        cid,
        finalOrderData,
        file || undefined
      );

      if (result.isGuestOrder && result.verificationCode) {
        alert(
          `주문이 생성되었습니다.\n\n비회원 인증번호: ${result.verificationCode}\n이 번호를 고객에게 전달해주세요.`
        );
      } else {
        alert("주문이 생성되었습니다.");
      }

      await refreshOrders();
      navigate(-1);
    } catch (e: any) {
      console.error(e);
      setErrorMessageTitle("주문 생성 실패");
      setErrorMessage(e?.message ?? "주문 생성에 실패했습니다.");
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="h-full bg-gray-50 overflow-hidden flex flex-col">
        {viewMode === "detail" && selectedOrder && (
          <OrderDetail
            companyId={Number(companyId)}
            order={selectedOrder}
            onStatusChange={handleStatusChange}
          />
        )}

        {viewMode === "create" && companyId && (
          <OrderCreateForm
            companyId={companyId}
            shopProducts={shopProducts}
            selectedProduct={selectedProduct}
            productOptions={productOptions}
            calculatedPrice={calculatedPrice}
            companyMembers={companyMembers}
            onBack={() => navigate(-1)}
            onProductSelect={handleProductSelect}
            onCalculatePrice={handleCalculatePrice}
            onSubmit={handleCreateOrder}
          />
        )}

        {viewMode === "createCustom" && companyId && (
          <CustomOrderCreateForm
            companyId={companyId}
            companyMembers={companyMembers}
            onBack={() => navigate(-1)}
            onSubmit={handleCreateOrder}
          />
        )}

        {viewMode === "list" && (
          <div className="flex-1 min-h-0 overflow-y-auto">
          <OrderList
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={(from, to) => {
              setStartDate(from);
              setEndDate(to);
            }}
            dateRangeInvalid={dateRangeInvalid}
            fetchSearchSuggestions={fetchSearchSuggestions}
            onShowStatistics={() => setIsStatisticsOpen(true)}
            refreshError={refreshError}
            orders={orders}
            searchKeyword={searchKeyword}
            selectedOrderStatus={selectedOrderStatus}
            isResetButtonEnabled={isResetButtonEnabled}
            onSearchKeywordChange={setSearchKeyword}
            onStatusFilterChange={setSelectedOrderStatus}
            onResetFilters={() => {
              setSearchKeyword("");
              setSelectedOrderStatus("all");
              setStartDate("");
              setEndDate("");
            }}
            onCreateOrder={() => setSearchParams({ mode: "create" })}
            onCreateCustomOrder={() => setSearchParams({ mode: "createCustom" })}
            onRowClick={(order) =>
              setSearchParams({ mode: "detail", orderId: order.id })
            }
            onLoadMore={loadMoreOrders}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
          />
          {isStatisticsOpen && companyId && (
            <OrderAmountSummaryDialog
              companyId={Number(companyId)}
              query={{ status: uiStatusToApiStatus(selectedOrderStatus), keyword: searchKeyword.trim(), dateFrom, dateTo }}
              conditionLabels={statisticsConditionLabels}
              onClose={closeStatistics}
            />
          )}
          </div>
        )}

        <ResubmissionDialog
          isOpen={isResubmissionOpen}
          message={resubmissionMessage}
          onMessageChange={setResubmissionMessage}
          onClose={() => setIsResubmissionOpen(false)}
          onConfirm={handleConfirmResubmission}
        />

        <ShippingDialog
          isOpen={isShippingDialogOpen}
          courier={shippingCourier}
          trackingNumber={shippingTrackingNumber}
          courierList={courierList}
          isLoading={isCourierLoading}
          onCourierChange={setShippingCourier}
          onTrackingNumberChange={setShippingTrackingNumber}
          onClose={() => setIsShippingDialogOpen(false)}
          onConfirm={handleConfirmShipping}
        />

        <ErrorMessageModal
          errorMessageTitle={errorMessageTitle}
          errorMessage={errorMessage}
          onClose={() => {
            setErrorMessageTitle("");
            setErrorMessage("");
          }}
        />
      </div>
    </CompanyAdminLayout>
  );
};

export default OrderManagement;

// Re-export types for backward compatibility
export type { Order, OrderStatus } from "./types";
export { ORDER_STATUS_LABELS } from "./constants";

